import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getCountFromServer,
  getDocs,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebaseConfig";
import {
  DEFAULT_FILTERS,
  REPORT_MODES,
  applyReportFilters,
  asString,
  buildCompletedDespatchRows,
  buildLocationLookup,
  buildNavMismatchRows,
  buildProductLookups,
  buildWarehouseRemovalRows,
  downloadMovementWorkbook,
  formatDateTime,
} from "./cylinderMovementReportHelper";

const MAX_CLIENT_COLLECTION_ROWS = 20000;

const MODE_DETAILS = {
  [REPORT_MODES.DESPATCH]: {
    label: "Completed despatch report scans",
    definition:
      "Shows each barcode scan belonging to a despatch report whose header is marked completed. It proves inclusion on a completed report; it does not prove physical departure.",
    dateHelp:
      "Date filters use the item scan time. Report creation time is displayed separately and neither timestamp is labelled as a departure time.",
  },
  [REPORT_MODES.WAREHOUSE]: {
    label: "Warehouse removal events",
    definition:
      "Shows records held in warehouse_archive. These records prove removal or archival from live warehouse data, not physical departure from Hortonwood.",
    dateHelp:
      "Date filters use BookedOutTime. Identifiable stocktake backups are excluded by default and can be shown with the classification filter.",
  },
  [REPORT_MODES.NAV]: {
    label: "NAV open-stock mismatch",
    definition:
      "Compares live warehouse stock occurrences with the complete loaded stock_sales_live collection using ProductId and SerialNumber. It does not prove invoicing.",
    dateHelp:
      "This is a current-state comparison, so movement date filters do not apply. NAV does not supply Special and ambiguous warehouse duplicates are not paired arbitrarily.",
  },
};

const BASE_INPUT_CLASS =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200";

function firestoreRows(snapshot) {
  return snapshot.docs.map((documentSnapshot) => ({
    id: documentSnapshot.id,
    ...documentSnapshot.data(),
  }));
}

function textCell(value) {
  return value === "" || value == null ? "—" : String(value);
}

function statusTone(status) {
  if (
    status === "No NAV open-stock match" ||
    status === "Invalid identity"
  ) {
    return "border-rose-200 bg-rose-50 text-rose-800";
  }
  if (
    status === "Ambiguous duplicate match" ||
    status === "Ambiguous product mapping" ||
    status === "Missing product mapping" ||
    status === "NAV comparison incomplete" ||
    status === "Unclassified removal"
  ) {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }
  if (
    status === "NAV match found" ||
    status === "Completed despatch scan"
  ) {
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
  }
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function StatusBadge({ value }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${statusTone(
        value
      )}`}
    >
      {value}
    </span>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}
      </span>
      {children}
    </label>
  );
}

function modeColumns(mode) {
  const identityColumns = [
    {
      key: "fullBarcode",
      label: "Full barcode",
      render: (row) => (
        <span className="font-mono">{textCell(row.fullBarcode)}</span>
      ),
    },
    {
      key: "serialNumber",
      label: "Serial",
      render: (row) => (
        <span className="font-mono">{textCell(row.serialNumber)}</span>
      ),
    },
    {
      key: "productId",
      label: "Product ID",
      render: (row) => (
        <span className="font-mono">{textCell(row.productId)}</span>
      ),
    },
    {
      key: "productCode",
      label: "Product code",
      render: (row) => textCell(row.productCode),
    },
    {
      key: "special",
      label: "Special / type",
      render: (row) =>
        textCell(
          row.specialRaw && row.specialRaw !== row.special
            ? `${row.special} (${row.specialRaw})`
            : row.special
        ),
    },
  ];

  if (mode === REPORT_MODES.DESPATCH) {
    return [
      ...identityColumns,
      {
        key: "reportNumber",
        label: "Report",
        render: (row) => textCell(row.reportNumber),
      },
      {
        key: "itemScannedAt",
        label: "Item scanned",
        render: (row) => textCell(formatDateTime(row.itemScannedAt)),
      },
      {
        key: "reportCreatedAt",
        label: "Report created",
        render: (row) => textCell(formatDateTime(row.reportCreatedAt)),
      },
      {
        key: "duplicateCount",
        label: "Occurrences",
        render: (row) =>
          row.isDuplicate
            ? `${row.occurrenceNumber} of ${row.duplicateCount}`
            : "1",
      },
      {
        key: "source",
        label: "Source IDs",
        render: (row) => (
          <span className="font-mono text-xs">
            Header: {textCell(row.reportHeaderId)}
            <br />
            Detail: {textCell(row.reportDetailId)}
            <br />
            Item: {textCell(row.itemId)}
          </span>
        ),
      },
      {
        key: "reason",
        label: "Why this row appears",
        render: (row) => row.reason,
      },
    ];
  }

  if (mode === REPORT_MODES.WAREHOUSE) {
    return [
      ...identityColumns,
      {
        key: "location",
        label: "Last known location",
        render: (row) => textCell(row.location),
      },
      {
        key: "bookedOutAt",
        label: "Booked out",
        render: (row) => textCell(formatDateTime(row.bookedOutAt)),
      },
      {
        key: "classification",
        label: "Classification",
        render: (row) => <StatusBadge value={row.classification} />,
      },
      {
        key: "duplicateCount",
        label: "Occurrences",
        render: (row) =>
          row.isDuplicate
            ? `${row.occurrenceNumber} of ${row.duplicateCount}`
            : "1",
      },
      {
        key: "removalDetails",
        label: "Removal details",
        render: (row) => (
          <span className="text-xs">
            Method: {textCell(row.removalMethod)}
            <br />
            Reason: {textCell(row.removalReason)}
            <br />
            Notes: {textCell(row.notes)}
            <br />
            Actor: {textCell(row.actor)}
          </span>
        ),
      },
      {
        key: "source",
        label: "Source IDs",
        render: (row) => (
          <span className="font-mono text-xs">
            Archive: {textCell(row.archiveDocumentId)}
            <br />
            Original: {textCell(row.originalSourceId)}
          </span>
        ),
      },
      {
        key: "reason",
        label: "Why this row appears",
        render: (row) => row.reason,
      },
    ];
  }

  return [
    ...identityColumns,
    {
      key: "location",
      label: "Warehouse location",
      render: (row) => textCell(row.location),
    },
    {
      key: "status",
      label: "NAV status",
      render: (row) => <StatusBadge value={row.status} />,
    },
    {
      key: "occurrences",
      label: "Occurrences",
      render: (row) => (
        <span className="text-xs">
          Warehouse: {row.warehouseOccurrenceCount}
          <br />
          NAV: {row.navOccurrenceCount}
        </span>
      ),
    },
    {
      key: "source",
      label: "Source IDs",
      render: (row) => (
        <span className="font-mono text-xs">
          Warehouse: {textCell(row.warehouseDocumentId)}
          <br />
          NAV:{" "}
          {row.navDocumentIds?.length
            ? row.navDocumentIds.join(", ")
            : "—"}
        </span>
      ),
    },
    {
      key: "reason",
      label: "Why this row appears",
      render: (row) => row.reason,
    },
  ];
}

export default function CylinderMovementReport() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(REPORT_MODES.DESPATCH);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [rows, setRows] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loadSummary, setLoadSummary] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);

  useEffect(() => {
    setFilters({ ...DEFAULT_FILTERS });
  }, [mode]);

  useEffect(() => {
    let cancelled = false;

    async function loadSelectedMode() {
      setLoading(true);
      setError("");
      setWarnings([]);
      setRows([]);
      setLoadSummary("");

      try {
        if (mode === REPORT_MODES.DESPATCH) {
          const [reportSnapshot, detailSnapshot, productSnapshot] =
            await Promise.all([
              getDocs(collection(db, "despatchreports")),
              getDocs(collection(db, "despatchreportsdetailed")),
              getDocs(collection(db, "cyl_info")),
            ]);
          if (cancelled) return;

          const reports = firestoreRows(reportSnapshot);
          const details = firestoreRows(detailSnapshot);
          const products = firestoreRows(productSnapshot);
          const { byId } = buildProductLookups(products);
          const completedReports = reports.filter(
            (report) => report.completed === true
          );
          const detailIds = new Set(details.map((detail) => asString(detail.id)));
          const missingDetailCount = completedReports.filter(
            (report) => !detailIds.has(asString(report.id))
          ).length;
          const builtRows = buildCompletedDespatchRows(
            reports,
            details,
            byId
          );
          const nextWarnings = [];
          if (missingDetailCount > 0) {
            nextWarnings.push(
              `${missingDetailCount} completed report header(s) have no matching detailed document and cannot supply cylinder rows.`
            );
          }

          setRows(builtRows);
          setWarnings(nextWarnings);
          setLoadSummary(
            `Loaded ${completedReports.length} completed headers, ${details.length} detail documents and ${builtRows.length} scan rows.`
          );
        } else if (mode === REPORT_MODES.WAREHOUSE) {
          const [archiveSnapshot, productSnapshot, locationSnapshot] =
            await Promise.all([
              getDocs(collection(db, "warehouse_archive")),
              getDocs(collection(db, "cyl_info")),
              getDocs(collection(db, "warehouse_locations")),
            ]);
          if (cancelled) return;

          const archive = firestoreRows(archiveSnapshot);
          const { byId } = buildProductLookups(
            firestoreRows(productSnapshot)
          );
          const locationMap = buildLocationLookup(
            firestoreRows(locationSnapshot)
          );
          const builtRows = buildWarehouseRemovalRows(
            archive,
            byId,
            locationMap
          );
          const stocktakeBackups = builtRows.filter(
            (row) => row.classification === "Stocktake backup"
          ).length;
          const unclassified = builtRows.filter(
            (row) => row.classification === "Unclassified removal"
          ).length;
          const nextWarnings = [
            "An archive record proves removal or archival from live warehouse data; it does not prove physical departure from Hortonwood.",
          ];
          if (stocktakeBackups > 0) {
            nextWarnings.push(
              `${stocktakeBackups} identifiable stocktake backup record(s) are excluded by default. Select “All classifications” or “Stocktake backup” to show them.`
            );
          }
          if (unclassified > 0) {
            nextWarnings.push(
              `${unclassified} archive record(s) do not identify the source removal workflow and are classified as unclassified.`
            );
          }

          setRows(builtRows);
          setWarnings(nextWarnings);
          setLoadSummary(
            `Loaded ${archive.length} archive records, ${productSnapshot.size} products and ${locationSnapshot.size} locations.`
          );
        } else {
          const navCollection = collection(db, "stock_sales_live");
          const warehouseCollection = collection(db, "cylinders_warehouse");
          const [navCountSnapshot, warehouseCountSnapshot] = await Promise.all([
            getCountFromServer(navCollection),
            getCountFromServer(warehouseCollection),
          ]);
          if (cancelled) return;

          const expectedNavCount = navCountSnapshot.data().count;
          const expectedWarehouseCount = warehouseCountSnapshot.data().count;

          if (
            expectedNavCount > MAX_CLIENT_COLLECTION_ROWS ||
            expectedWarehouseCount > MAX_CLIENT_COLLECTION_ROWS
          ) {
            setWarnings([
              `NAV comparison incomplete: the current collections contain ${expectedNavCount} NAV row(s) and ${expectedWarehouseCount} warehouse row(s), exceeding the first-release browser safety threshold of ${MAX_CLIENT_COLLECTION_ROWS} rows per collection.`,
              "No definitive NAV absence results have been produced. Backend pagination or aggregation must be reviewed before this mode can run at this volume.",
            ]);
            setLoadSummary("Collection counts were checked; comparison was not run.");
            setRows([]);
            return;
          }

          const [
            navSnapshot,
            warehouseSnapshot,
            productSnapshot,
            locationSnapshot,
          ] = await Promise.all([
            getDocs(navCollection),
            getDocs(warehouseCollection),
            getDocs(collection(db, "cyl_info")),
            getDocs(collection(db, "warehouse_locations")),
          ]);
          if (cancelled) return;

          const navDocuments = firestoreRows(navSnapshot);
          const warehouseDocuments = firestoreRows(warehouseSnapshot);
          const { byId, idsByCode } = buildProductLookups(
            firestoreRows(productSnapshot)
          );
          const locationMap = buildLocationLookup(
            firestoreRows(locationSnapshot)
          );
          const comparisonComplete =
            navSnapshot.size === expectedNavCount &&
            warehouseSnapshot.size === expectedWarehouseCount;
          const { rows: builtRows, diagnostics } = buildNavMismatchRows({
            warehouseDocuments,
            navDocuments,
            productById: byId,
            productIdsByCode: idsByCode,
            locationBySuffix: locationMap,
            navComplete: comparisonComplete,
          });

          const nextWarnings = [
            comparisonComplete
              ? `The complete Firestore collections were loaded without the previous 2,000/5,000 query caps (${navSnapshot.size} NAV rows and ${warehouseSnapshot.size} warehouse rows).`
              : "NAV comparison incomplete: collection counts changed while data was loading, so unmatched records are not labelled as definitively missing.",
            "Completeness here means the complete available Firestore collections were loaded. This page cannot verify the external NAV synchronization process or prove invoicing.",
          ];
          if (diagnostics.missingProductMappings > 0) {
            nextWarnings.push(
              `${diagnostics.missingProductMappings} NAV row(s) could not be mapped from product code to cyl_info.`
            );
          }
          if (diagnostics.ambiguousProductMappings > 0) {
            nextWarnings.push(
              `${diagnostics.ambiguousProductMappings} NAV row(s) map to multiple ProductIds and were not paired automatically.`
            );
          }
          if (diagnostics.invalidNavRows > 0) {
            nextWarnings.push(
              `${diagnostics.invalidNavRows} NAV row(s) had unusable identity data. Related absence results are marked incomplete.`
            );
          }

          setRows(builtRows);
          setWarnings(nextWarnings);
          setLoadSummary(
            `Compared ${navSnapshot.size} NAV rows with ${warehouseSnapshot.size} live warehouse rows; ${builtRows.length} live stock occurrence(s) are shown.`
          );
        }
      } catch (loadError) {
        if (!cancelled) {
          console.error("Cylinder movement report load failed:", loadError);
          setError(loadError?.message || String(loadError));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadSelectedMode();
    return () => {
      cancelled = true;
    };
  }, [mode, reloadVersion]);

  const filteredRows = useMemo(
    () => applyReportFilters(rows, filters, mode),
    [rows, filters, mode]
  );

  const classificationOptions = useMemo(() => {
    return Array.from(
      new Set(
        rows
          .map((row) => row.classification || row.status)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const columns = useMemo(() => modeColumns(mode), [mode]);
  const modeDetails = MODE_DETAILS[mode];

  function setFilter(name, value) {
    setFilters((current) => ({ ...current, [name]: value }));
  }

  function clearFilters() {
    setFilters({ ...DEFAULT_FILTERS });
  }

  function exportResults() {
    downloadMovementWorkbook({
      mode,
      rows: filteredRows,
      filters,
      warnings,
    });
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      <div className="mx-auto max-w-[1800px]">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Cylinder Movement and Reconciliation Report
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Read-only reporting across completed despatch scans, warehouse
              archive events, and the available NAV open-stock feed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/AdminDashboard")}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Back to Admin Dashboard
          </button>
        </div>

        <div className="mb-4 grid gap-2 md:grid-cols-3">
          {Object.entries(MODE_DETAILS).map(([modeId, details]) => (
            <button
              key={modeId}
              type="button"
              onClick={() => setMode(modeId)}
              className={`rounded-lg border p-4 text-left transition ${
                mode === modeId
                  ? "border-blue-600 bg-blue-50 text-blue-900 shadow-sm"
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
              }`}
            >
              <span className="block font-semibold">{details.label}</span>
            </button>
          ))}
        </div>

        <section className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h2 className="font-semibold text-blue-950">{modeDetails.label}</h2>
          <p className="mt-1 text-sm text-blue-900">
            {modeDetails.definition}
          </p>
          <p className="mt-1 text-sm text-blue-800">
            {modeDetails.dateHelp}
          </p>
        </section>

        {warnings.length > 0 && (
          <section
            className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4"
            role="status"
          >
            <h2 className="font-semibold text-amber-950">
              Data and interpretation warnings
            </h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-semibold text-slate-900">Filters</h2>
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Clear filters
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            {mode !== REPORT_MODES.NAV && (
              <>
                <FilterField label="Date from">
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(event) =>
                      setFilter("dateFrom", event.target.value)
                    }
                    className={BASE_INPUT_CLASS}
                  />
                </FilterField>
                <FilterField label="Date to">
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(event) =>
                      setFilter("dateTo", event.target.value)
                    }
                    className={BASE_INPUT_CLASS}
                  />
                </FilterField>
              </>
            )}

            <FilterField label="Serial number">
              <input
                value={filters.serialNumber}
                onChange={(event) =>
                  setFilter("serialNumber", event.target.value)
                }
                className={BASE_INPUT_CLASS}
                placeholder="Serial"
              />
            </FilterField>
            <FilterField label="Product ID">
              <input
                value={filters.productId}
                onChange={(event) =>
                  setFilter("productId", event.target.value)
                }
                className={BASE_INPUT_CLASS}
                placeholder="Product ID"
              />
            </FilterField>
            <FilterField label="Product code">
              <input
                value={filters.productCode}
                onChange={(event) =>
                  setFilter("productCode", event.target.value)
                }
                className={BASE_INPUT_CLASS}
                placeholder="Product code"
              />
            </FilterField>
            <FilterField label="Special / type">
              <input
                value={filters.special}
                onChange={(event) =>
                  setFilter("special", event.target.value)
                }
                className={BASE_INPUT_CLASS}
                placeholder="49, 50, ORDER…"
              />
            </FilterField>

            {mode !== REPORT_MODES.DESPATCH && (
              <FilterField label="Warehouse location">
                <input
                  value={filters.location}
                  onChange={(event) =>
                    setFilter("location", event.target.value)
                  }
                  className={BASE_INPUT_CLASS}
                  placeholder="Location"
                />
              </FilterField>
            )}

            {mode === REPORT_MODES.DESPATCH && (
              <FilterField label="Despatch report number">
                <input
                  value={filters.despatchReportNumber}
                  onChange={(event) =>
                    setFilter("despatchReportNumber", event.target.value)
                  }
                  className={BASE_INPUT_CLASS}
                  placeholder="Report number"
                />
              </FilterField>
            )}

            <FilterField
              label={
                mode === REPORT_MODES.NAV
                  ? "Reconciliation status"
                  : "Classification"
              }
            >
              <select
                value={filters.classification}
                onChange={(event) =>
                  setFilter("classification", event.target.value)
                }
                className={BASE_INPUT_CLASS}
              >
                <option value="">
                  {mode === REPORT_MODES.WAREHOUSE
                    ? "Default removals (no backups)"
                    : "All"}
                </option>
                {mode === REPORT_MODES.WAREHOUSE && (
                  <option value="__all__">All classifications</option>
                )}
                {classificationOptions.map((classification) => (
                  <option key={classification} value={classification}>
                    {classification}
                  </option>
                ))}
              </select>
            </FilterField>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
            <div>
              <span className="font-semibold text-slate-900">
                {filteredRows.length} result
                {filteredRows.length === 1 ? "" : "s"}
              </span>
              {loadSummary && (
                <p className="mt-1 text-xs text-slate-500">{loadSummary}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setReloadVersion((value) => value + 1)}
                disabled={loading}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Reload
              </button>
              <button
                type="button"
                onClick={exportResults}
                disabled={loading || filteredRows.length === 0}
                className="rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Export filtered XLSX
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-600" role="status">
              Loading {modeDetails.label.toLowerCase()}…
            </div>
          ) : error ? (
            <div className="p-6" role="alert">
              <h2 className="font-semibold text-rose-800">
                The report could not be loaded
              </h2>
              <p className="mt-1 text-sm text-rose-700">{error}</p>
              <button
                type="button"
                onClick={() => setReloadVersion((value) => value + 1)}
                className="mt-3 rounded-md bg-rose-700 px-3 py-2 text-sm font-semibold text-white"
              >
                Try again
              </button>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-10 text-center text-slate-600">
              No rows match the selected mode and filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead className="sticky top-0 bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="whitespace-nowrap border-b border-slate-200 px-3 py-3"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr
                      key={row.rowId}
                      className={`align-top hover:bg-slate-50 ${
                        row.isDuplicate ? "bg-amber-50/50" : ""
                      }`}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="max-w-sm border-b border-slate-100 px-3 py-3 text-slate-700"
                        >
                          {column.render(row)}
                          {column.key === "fullBarcode" &&
                            row.isDuplicate && (
                              <span className="mt-1 block text-xs font-semibold text-amber-700">
                                Repeated occurrence
                              </span>
                            )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
